import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const PluginBasicInformationDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PluginBasicInformation"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"idPlugin"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"plugin"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"idPlugin"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"author"}},{"kind":"Field","name":{"kind":"Name","value":"categories"}},{"kind":"Field","name":{"kind":"Name","value":"email"}},{"kind":"Field","name":{"kind":"Name","value":"icon"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"url"}}]}},{"kind":"Field","name":{"kind":"Name","value":"iframeConnectorUrl"}},{"kind":"Field","name":{"kind":"Name","value":"public"}},{"kind":"Field","name":{"kind":"Name","value":"supportEmail"}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"PluginBasicInformation","document":PluginBasicInformationDocument}} as const;
export type PluginBasicInformationQueryVariables = Types.Exact<{
  idPlugin: Types.Scalars['ID']['input'];
}>;


export type PluginBasicInformationQuery = (
  { __typename: 'Query' }
  & { plugin?: Types.Maybe<(
    { __typename: 'Plugin' }
    & Pick<Types.Plugin, 'id' | 'author' | 'categories' | 'email' | 'iframeConnectorUrl' | 'public' | 'supportEmail'>
    & { icon: (
      { __typename: 'Plugin_Icon' }
      & Pick<Types.Plugin_Icon, 'url'>
    ) }
  )> }
);

/**
 * __usePluginBasicInformationQuery__
 *
 * To run a query within a React component, call `usePluginBasicInformationQuery` and pass it any options that fit your needs.
 * When your component renders, `usePluginBasicInformationQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePluginBasicInformationQuery({
 *   variables: {
 *      idPlugin: // value for 'idPlugin'
 *   },
 * });
 */
export function usePluginBasicInformationQuery(
  baseOptions: TrelloQueryHookOptions<
    PluginBasicInformationQuery,
    PluginBasicInformationQueryVariables
  > &
    (
      | { variables: PluginBasicInformationQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: PluginBasicInformationDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    PluginBasicInformationQuery,
    PluginBasicInformationQueryVariables
  >(PluginBasicInformationDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function usePluginBasicInformationLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    PluginBasicInformationQuery,
    PluginBasicInformationQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    PluginBasicInformationQuery,
    PluginBasicInformationQueryVariables
  >(PluginBasicInformationDocument, options);
}
export function usePluginBasicInformationSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        PluginBasicInformationQuery,
        PluginBasicInformationQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    PluginBasicInformationQuery,
    PluginBasicInformationQueryVariables
  >(PluginBasicInformationDocument, options);
}
export type PluginBasicInformationQueryHookResult = ReturnType<
  typeof usePluginBasicInformationQuery
>;
export type PluginBasicInformationLazyQueryHookResult = ReturnType<
  typeof usePluginBasicInformationLazyQuery
>;
export type PluginBasicInformationSuspenseQueryHookResult = ReturnType<
  typeof usePluginBasicInformationSuspenseQuery
>;
export type PluginBasicInformationQueryResult = Apollo.QueryResult<
  PluginBasicInformationQuery,
  PluginBasicInformationQueryVariables
>;
